<template>
	<div class="mb-4">
		<div
			class="main-wrapper mt-4"
		>
			<CRow>
				<!-- General Section -->
				<CCol
					md="12"
					class="d-flex justify-content-between align-items-center mb-4"
				>
					<h4>
						General Info
					</h4>
					<div class="marketing-category-id">
						<span class="color-black-45 mr-1">Banner in search ID:</span>
						<span class="color-black-45 font-weight-bold">
							{{ isEdit ? $route.params.id : 'Generate after saving' }}
						</span>
					</div>
				</CCol>
				<!-- End General Section -->

				<!-- Name Section -->
				<CCol md="12">
					<CInput
						v-model="$v.form.name.$model"
						:is-valid="!$v.form.name.$error && null"
						:invalid-feedback="$t('global.error.required')"
						label="Banner in search name*"
						placeholder="Banner in search name"
						data-test-id="banner-in-search-name"
					/>
				</CCol>
				<!-- End Name Section -->

				<!-- Status Section -->
				<CCol md="12">
					<CRow>
						<CCol>
							<div class="form-group mt-2">
								<p class="label label-btn-group">
									Banner in search status
								</p>
								<BaseButtonGroup
									v-model="form.isActive"
									:list="STATUS_LIST"
									data-test-id="banner-in-search-status"
								/>
							</div>
						</CCol>
					</CRow>
				</CCol>
				<!-- End Status Section -->

				<CCol md="12">
					<hr class="my-4">
				</CCol>

				<!-- Date time Section -->
				<CCol md="12">
					<ActiveSchedule
						ref="date"
						class="active-schedule"
						is-require
						required-end-date-time
						:start-date="form.startDate"
						:start-time="form.startTime"
						:end-date="form.endDate"
						:end-time="form.endTime"
						@update:startDate="handleSetStartDate"
						@update:startTime="form.startTime = $event"
						@update:endDate="handleSetEndDate"
						@update:endTime="form.endTime = $event"
					/>
				</CCol>
				<!-- End Date time Section -->

				<CCol md="12">
					<hr class="my-4">
				</CCol>
				<!-- Category Section -->
				<CCol md="12">
					<BaseCategorySelectorBox
						ref="categorySelectorBox"
						:categories="form.categories"
						label="Select the category where the banner should be displayed while users are searching."
						:invalid-feedback="$t('global.error.required')"
						is-require-select
						@onSelected="handleSelectedCategory"
					/>
					<div
						v-if="$v.form.categories.$error"
						class="invalid-feedback d-block"
					>
						{{ $t('global.error.required') }}
					</div>
				</CCol>
				<!-- End Category Section -->

				<CCol md="12">
					<hr class="my-4">
				</CCol>

				<CCol md="12">
					<StoreFrontVisibility
						id="banner-in-search-visibility"
						label="Banner in search"
						image-url="/assets/images/banner-in-search.svg"
						image-alt="Banner in search"
						:can-set-visibility="false"
						description="Banner will be displayed on the search page, with two sizes optimized for desktop and mobile."
						:md="5"
						:message-md="7"
					/>
				</CCol>

				<CCol md="12">
					<BaseMediaSelectorBox
						ref="desktopBannerTh"
						v-model="form.media.desktopBannerTh"
						title="Desktop banner TH"
						no-image-text="No banner"
						show-helper
						width="440"
						height="200"
						class="mt-4"
						required
					/>
				</CCol>

				<CCol md="12">
					<BaseMediaSelectorBox
						ref="desktopBannerEn"
						v-model="form.media.desktopBannerEn"
						title="Desktop banner EN"
						no-image-text="No banner"
						show-helper
						width="440"
						height="200"
						class="mt-4"
						required
					/>
				</CCol>

				<CCol md="12">
					<BaseMediaSelectorBox
						ref="mobileBannerTh"
						v-model="form.media.mobileBannerTh"
						title="Mobile banner TH"
						no-image-text="No banner"
						show-helper
						width="288"
						height="136"
						class="mt-4"
						required
					/>
				</CCol>

				<CCol md="12">
					<BaseMediaSelectorBox
						ref="mobileBannerEn"
						v-model="form.media.mobileBannerEn"
						title="Mobile banner EN"
						no-image-text="No banner"
						show-helper
						width="288"
						height="136"
						class="mt-4"
						required
					/>
				</CCol>

				<CCol md="12">
					<CInput
						ref="link"
						v-model.trim="$v.form.link.$model"
						:is-valid="!$v.form.link.$error && null"
						:invalid-feedback="$t('global.error.url')"
						label="Link"
					/>
				</CCol>
			</CRow>

			<BaseActionPanelStickyFooter
				:disabled-confirm="isSubmitting"
				:disabled-cancel="isSubmitting"
				:disabled-remove="isSubmitting"
				:is-edit="isEdit"
				:remove-text="canRemove"
				@onCancel="handleCancel"
				@onConfirm="handleSubmit"
				@onRemove="handleModalRemove"
			/>
		</div>

		<!-- Modal Section -->
		<BaseModalConfirmDelete
			ref="modal-remove"
			:handle-remove="handleRemove"
			title="Remove this banner in search"
			description="Are you sure you want to delete this banner in search?"
			@onSuccess="$router.push({ name: ROUTE_NAME.BANNER_IN_SEARCH })"
		/>
	</div>
</template>

<script>
import { required, url } from 'vuelidate/lib/validators';
import isEmpty from 'lodash/isEmpty';

import ActiveSchedule from '@/components/ActiveSchedule.vue';
import StoreFrontVisibility from '@/components/StoreFrontVisibility.vue';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';
import BaseCategorySelectorBox from '@/components/BaseCategorySelectorBox.vue';

import { ROUTE_NAME } from '../enums/route';
import {
	STATUS_LIST,
} from '../enums/status';

export default {
	name: 'BannerInSearchForm',

	components: {
		ActiveSchedule,
		StoreFrontVisibility,
		BaseMediaSelectorBox,
		BaseCategorySelectorBox,
	},

	props: {
		isEdit: {
			type: Boolean,
			default: false,
		},
		defaultData: {
			type: Object,
			default: null,
		},
		isSubmitting: {
			type: Boolean,
			default: false,
		},
	},

	validations() {
		return {
			form: {
				name: {
					required,
				},
				isActive: {
					required,
				},
				link: {
					url,
				},
				categories: {
					required,
				},
			},
		};
	},

	data() {
		return {
			ROUTE_NAME,
			STATUS_LIST,
			form: {
				name: '',
				isActive: 0,
				startDate: null,
				startTime: null,
				endDate: null,
				endTime: null,
				link: '',
				categories: [],
				media: {
					desktopBannerTh: null,
					desktopBannerEn: null,
					mobileBannerTh: null,
					mobileBannerEn: null,
				},
			},
		};
	},

	computed: {
		canRemove() {
			return this.isEdit ? 'Remove Banner in search' : '';
		},
	},

	created() {
		if (!isEmpty(this.defaultData)) {
			this.form = {
				...this.form,
				...this.defaultData,
			};
		}
	},

	methods: {
		// Datetime
		handleSetStartDate(date) {
			this.form.startDate = date;
			if (!this.form.startTime) {
				this.form.startTime = '00:00';
			}
		},
		handleSetEndDate(date) {
			this.form.endDate = date;
			if (!this.form.endTime) {
				this.form.endTime = '23:59';
			}
		},
		handleSelectedCategory(categories) {
			this.form.categories = categories;
		},

		// Footer section
		handleModalRemove() {
			this.$refs['modal-remove'].open();
		},
		handleCancel() {
			this.$router.push({ name: ROUTE_NAME.BANNER_IN_SEARCH });
		},
		handleRemove() {
			return new Promise((resolve, reject) => {
				this.$emit('onRemove', { resolve, reject });
			});
		},
		handleSubmit() {
			this.$v.$touch();
			const dateRef = this.$refs.date.$v;
			const desktopBannerThRef = this.$refs.desktopBannerTh.$v;
			const desktopBannerEnRef = this.$refs.desktopBannerEn.$v;
			const mobileBannerThRef = this.$refs.mobileBannerTh.$v;
			const mobileBannerEnRef = this.$refs.mobileBannerEn.$v;

			dateRef.$touch();
			desktopBannerThRef.$touch();
			desktopBannerEnRef.$touch();
			mobileBannerThRef.$touch();
			mobileBannerEnRef.$touch();

			if (this.$v.$invalid
				|| dateRef.$invalid
				|| desktopBannerThRef.$invalid
				|| desktopBannerEnRef.$invalid
				|| mobileBannerThRef.$invalid
				|| mobileBannerEnRef.$invalid
			) {
				return;
			}
			this.$emit('onSubmit', this.form);
		},
	},
};
</script>

<style lang="scss" scoped>
.active-schedule::v-deep {
	.input-container {
		margin-bottom: 0 !important;
	}
}
</style>